import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useMeta } from "@/hooks/useMeta";

export function PaymentSuccess() {
  useMeta("Order confirmation");
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState<any>({ status: "checking" });

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState({ status: "error" }); return; }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { clear(); setState({ status: "paid", order: r.data.order_number }); return; }
        if (r.data.payment_status === "expired" || r.data.payment_status === "failed") { setState({ status: "failed" }); return; }
      } catch { /* retry */ }
      if (++tries < 12 && !stop) setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
    return () => { stop = true; };
  }, []);

  return (
    <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="payment-success-page">
      {state.status === "checking" && <><Loader2 className="animate-spin mx-auto mb-6" /><p className="font-display text-3xl" data-testid="payment-status-checking">Confirming your payment…</p></>}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={44} strokeWidth={1} className="mx-auto mb-6 text-[var(--c-primary)]" />
          <h1 className="font-display text-4xl sm:text-5xl font-light" data-testid="payment-status-paid">Thank you</h1>
          <p className="mt-5 text-neutral-600">Your order <strong data-testid="order-number">{state.order}</strong> is confirmed. A confirmation email is on its way.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to={`/track?order=${state.order}`} className="btn-ink px-8 py-4 text-xs tracking-[0.25em] uppercase" data-testid="success-track-link">Track Order</Link>
            <Link to="/collections/all" className="btn-line px-8 py-4 text-xs tracking-[0.25em] uppercase">Continue Shopping</Link>
          </div>
        </>
      )}
      {["failed", "timeout", "error"].includes(state.status) && (
        <>
          <h1 className="font-display text-4xl font-light" data-testid="payment-status-failed">We couldn't confirm your payment</h1>
          <p className="mt-5 text-neutral-600">If you were charged, your order will appear in your account shortly. Otherwise please try again.</p>
          <Link to="/checkout" className="btn-ink inline-block mt-10 px-8 py-4 text-xs tracking-[0.25em] uppercase">Return to Checkout</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  useMeta("Payment cancelled");
  return (
    <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="payment-cancel-page">
      <h1 className="font-display text-4xl font-light">Checkout cancelled</h1>
      <p className="mt-5 text-neutral-600">Your bag has been saved. You can complete your purchase whenever you're ready.</p>
      <Link to="/checkout" className="btn-ink inline-block mt-10 px-8 py-4 text-xs tracking-[0.25em] uppercase" data-testid="cancel-return-checkout">Return to Checkout</Link>
    </div>
  );
}
